import { useEffect, useRef, useState, useCallback } from 'react';

// Configuration constants
const TAWK_LOAD_TIMEOUT = 10000; // 10 seconds timeout
const LAZY_LOAD_DELAY = 2000; // 2 seconds delay for lazy loading
const RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 5000; // 5 seconds between retries

// Performance monitoring
const performanceMarks = {
  start: 'tawk-widget-load-start',
  end: 'tawk-widget-load-end',
  timeout: 'tawk-widget-load-timeout',
  error: 'tawk-widget-load-error'
};

class TawkPerformanceMonitor {
  constructor() {
    this.metrics = {
      loadTimes: [],
      errors: [],
      timeouts: 0,
      retries: 0
    };
    this.isEnabled = process.env.NODE_ENV === 'development';
  }

  markStart() {
    if (this.isEnabled) {
      performance.mark(performanceMarks.start);
    }
  }

  markEnd() {
    if (this.isEnabled) {
      performance.mark(performanceMarks.end);
      performance.measure('tawk-widget-load', performanceMarks.start, performanceMarks.end);
      
      const measure = performance.getEntriesByName('tawk-widget-load')[0];
      if (measure) {
        this.metrics.loadTimes.push(measure.duration);
      }
    }
  }

  markTimeout() {
    if (this.isEnabled) {
      performance.mark(performanceMarks.timeout);
      this.metrics.timeouts++;
    }
  }

  markError(error) {
    if (this.isEnabled) {
      performance.mark(performanceMarks.error);
      this.metrics.errors.push({
        timestamp: Date.now(),
        error: error.message || error
      });
    }
  }

  incrementRetries() {
    this.metrics.retries++;
  }

  getMetrics() {
    return {
      ...this.metrics,
      averageLoadTime: this.metrics.loadTimes.length > 0 
        ? this.metrics.loadTimes.reduce((a, b) => a + b, 0) / this.metrics.loadTimes.length 
        : 0
    };
  }

  exportMetrics() {
    return {
      metrics: this.getMetrics(),
      performanceEntries: performance.getEntriesByType('measure'),
      userAgent: navigator.userAgent,
      timestamp: Date.now()
    };
  }
}

// Global performance monitor instance
const performanceMonitor = new TawkPerformanceMonitor();

// Widget URL mapping
const getWidgetUrl = (company) => {
  if (company.includes("Seven Star Jasem")) {
    return 'https://embed.tawk.to/677c886649e2fd8dfe037085/1igv79kcb';
  } else if (company.includes("7-Star")) {
    return 'https://embed.tawk.to/677c884349e2fd8dfe037082/1igv78ija';
  } else if (company.includes("Syzygy")) {
    return 'https://embed.tawk.to/677c88f5af5bfec1dbe79572/1igv7e05k';
  } else {
    return 'https://embed.tawk.to/666a5148981b6c56477c93f6/1i07kr15t';
  }
};

// Load Tawk.to widget with timeout and retry logic
const loadTawkWidget = async (widgetUrl, retryCount = 0) => {
  return new Promise((resolve, reject) => {
    performanceMonitor.markStart();
    
    const timeoutId = setTimeout(() => {
      performanceMonitor.markTimeout();
      console.warn(`Tawk.to widget loading timed out (attempt ${retryCount + 1})`);
      reject(new Error('Tawk.to widget loading timed out'));
    }, TAWK_LOAD_TIMEOUT);

    try {
      const script = document.createElement('script');
      script.async = true;
      script.src = widgetUrl;
      script.charset = 'UTF-8';
      script.setAttribute('crossorigin', '*');
      
      script.onload = () => {
        clearTimeout(timeoutId);
        performanceMonitor.markEnd();
        console.log('Tawk.to widget loaded successfully');
        resolve(true);
      };
      
      script.onerror = (error) => {
        clearTimeout(timeoutId);
        performanceMonitor.markError(error);
        console.error('Tawk.to widget failed to load:', error);
        reject(error);
      };
      
      document.body.appendChild(script);
    } catch (error) {
      clearTimeout(timeoutId);
      performanceMonitor.markError(error);
      reject(error);
    }
  });
};

// Lazy loading with intersection observer
const createLazyLoadObserver = (callback) => {
  if (!window.IntersectionObserver) {
    // Fallback for older browsers
    setTimeout(callback, LAZY_LOAD_DELAY);
    return null;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        callback();
        observer.disconnect();
      }
    });
  }, {
    rootMargin: '50px', // Start loading when widget is 50px from viewport
    threshold: 0.1
  });

  return observer;
};

export const useTawkWidget = (user, options = {}) => {
  const {
    lazyLoad = true,
    backgroundLoad = true,
    enableMonitoring = true,
    onLoad,
    onError,
    onTimeout
  } = options;

  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const observerRef = useRef(null);
  const loadAttemptedRef = useRef(false);
  const widgetUrlRef = useRef(null);

  // Get widget URL based on user company
  useEffect(() => {
    if (user?.company) {
      widgetUrlRef.current = getWidgetUrl(user.company);
    }
  }, [user?.company]);

  // Background loading function
  const loadWidget = useCallback(async (isRetry = false) => {
    if (!widgetUrlRef.current || loadAttemptedRef.current) return;

    setIsLoading(true);
    setError(null);
    
    if (isRetry) {
      performanceMonitor.incrementRetries();
      setRetryCount(prev => prev + 1);
    }

    try {
      await loadTawkWidget(widgetUrlRef.current, retryCount);
      setIsLoaded(true);
      loadAttemptedRef.current = true;
      onLoad?.();
    } catch (error) {
      setError(error);
      onError?.(error);
      
      // Retry logic
      if (retryCount < RETRY_ATTEMPTS - 1) {
        setTimeout(() => {
          loadWidget(true);
        }, RETRY_DELAY);
      } else {
        onTimeout?.();
      }
    } finally {
      setIsLoading(false);
    }
  }, [retryCount, onLoad, onError, onTimeout]);

  // Background loading effect
  useEffect(() => {
    if (backgroundLoad && widgetUrlRef.current && !lazyLoad) {
      // Load immediately in background
      loadWidget();
    }
  }, [backgroundLoad, lazyLoad, loadWidget]);

  // Lazy loading effect
  useEffect(() => {
    if (lazyLoad && widgetUrlRef.current && !loadAttemptedRef.current) {
      // Create intersection observer for lazy loading
      observerRef.current = createLazyLoadObserver(() => {
        loadWidget();
      });

      // Observe the document body for lazy loading trigger
      if (observerRef.current) {
        observerRef.current.observe(document.body);
      }
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [lazyLoad, loadWidget]);

  // Manual load function
  const manualLoad = useCallback(() => {
    if (!isLoaded && !isLoading) {
      loadWidget();
    }
  }, [isLoaded, isLoading, loadWidget]);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    loadAttemptedRef.current = false;
  }, []);

  // Expose performance monitor in development
  useEffect(() => {
    if (enableMonitoring && process.env.NODE_ENV === 'development') {
      window.tawkPerformanceMonitor = performanceMonitor;
    }
  }, [enableMonitoring]);

  return {
    isLoading,
    isLoaded,
    error,
    retryCount,
    manualLoad,
    cleanup,
    performanceMetrics: performanceMonitor.getMetrics()
  };
};

// Export performance monitor for external use
export { performanceMonitor };